import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../utils/constants';

/**
 * Custom hook to fetch and manage available dates for hearing pages
 * @param {string} endpoint - API endpoint to fetch dates (e.g., 'AvailableFirstHearingDates')
 * @param {number} arbId - Arbitrator ID
 * @returns {Object} - { availableDates, availableDateObjects, openToDate, isLoading, error }
 */
export const useAvailableDates = (endpoint, arbId) => {
    const [availableDates, setAvailableDates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!arbId || !endpoint) return;

        const fetchAvailableDates = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const response = await fetch(
                    `${API_BASE_URL}/api/${endpoint}?Arb_id=${arbId}`
                );

                if (response.ok) {
                    const result = await response.json();
                    setAvailableDates(result.data || []);
                } else {
                    throw new Error('Failed to fetch available dates');
                }
            } catch (err) {
                console.error('Error fetching available dates:', err);
                setError(err.message);
                setAvailableDates([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAvailableDates();
    }, [endpoint, arbId]);

    // Parse available dates to Date objects for react-datepicker
    // Parse as local date to avoid timezone issues
    const availableDateObjects = availableDates.map(dateStr => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
    });

    // The calendar opens on the next hearing day (or the latest one if all are past), not on today's month
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const openToDate =
        availableDateObjects.find((d) => d >= today) || availableDateObjects[availableDateObjects.length - 1] || undefined;

    return {
        availableDates,
        availableDateObjects,
        openToDate,
        isLoading,
        error
    };
};
